import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { BatteryCharging, BarChart3, GraduationCap, Check, Bell } from "lucide-react";
import { NotifyMeModal } from "../components/NotifyMeModal";

const MANUFACTURERS = [
  { name: "Tesla", status: "Live · full API", color: "#39FF14" },
  { name: "Ford", status: "Live · full API", color: "#00A2FF" },
  { name: "Volvo", status: "Live · full API", color: "#9CC7E8" },
  { name: "Polestar", status: "OEM API Pending", color: "#D8DEE4", pending: true },
  { name: "BMW", status: "OEM API Pending", color: "#4A90D9", pending: true, notify: true },
  { name: "Audi", status: "OEM API Pending", color: "#C13030", pending: true },
  { name: "Rivian", status: "OEM API Pending", color: "#E8B84A", pending: true },
  { name: "Lucid Motors", status: "OEM API Pending", color: "#B8A88A", pending: true },
  { name: "GM (Ultium)", status: "OEM API Pending", color: "#71717A", pending: true, notify: true },
  { name: "Hyundai", status: "Coming soon", color: "#71717A", coming_soon: true },
  { name: "Kia", status: "Coming soon", color: "#71717A", coming_soon: true },
];

export const About = () => {
  const { t } = useTranslation();
  const [notify, setNotify] = useState({ open: false, make: "" });
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-14 sm:py-20 lg:py-28 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mb-4">
              {t("about.eyebrow")}
            </div>
            <h2 className="font-display text-3xl sm:text-5xl font-semibold leading-[1] text-white">
              {t("about.title_1")} <span className="brand-text-gradient">{t("about.title_gradient")}</span>
            </h2>
          </div>

          <div className="lg:col-span-7">
            <p
              className="text-base sm:text-lg lg:text-xl text-zinc-300 leading-relaxed"
              data-testid="about-paragraph"
            >
              {t("about.body")}
            </p>
            <p className="mt-6 text-base lg:text-lg text-zinc-400 leading-relaxed">
              {t("about.roadmap")}
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Pillar
                icon={BatteryCharging}
                label={t("about.pillar_1")}
                desc={t("about.pillar_1_desc")}
              />
              <Pillar
                icon={BarChart3}
                label={t("about.pillar_2")}
                desc={t("about.pillar_2_desc")}
              />
              <Pillar
                icon={GraduationCap}
                label={t("about.pillar_3")}
                desc={t("about.pillar_3_desc")}
              />
            </div>

            {/* Multi-manufacturer roadmap grid */}
            <div className="mt-10">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-3">
                Manufacturer coverage roadmap
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="manufacturer-roadmap">
                {MANUFACTURERS.map((m) => (
                  <div
                    key={m.name}
                    className="rounded-lg border p-2.5 flex items-center gap-2"
                    style={{
                      borderColor: m.pending ? "rgba(255,176,32,0.16)" : m.coming_soon ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.10)",
                      background: m.coming_soon ? "rgba(0,0,0,0.18)" : "rgba(0,0,0,0.30)",
                      opacity: m.coming_soon ? 0.55 : m.pending ? 0.85 : 1,
                    }}
                    data-testid={`manufacturer-${m.name.toLowerCase().split(" ")[0]}`}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ background: m.color, boxShadow: (m.coming_soon || m.pending) ? "none" : `0 0 6px ${m.color}` }}
                    />
                    <div className="min-w-0 flex-1">
                      <div
                        className="text-xs font-medium truncate"
                        style={{
                          color: m.coming_soon ? "#a1a1aa" : "#ffffff",
                          textDecoration: m.coming_soon ? "line-through" : "none",
                          textDecorationColor: m.coming_soon ? "rgba(255,255,255,0.35)" : undefined,
                          textDecorationThickness: m.coming_soon ? "1px" : undefined,
                        }}
                      >
                        {m.name}
                      </div>
                      <div
                        className="font-mono text-[9px] truncate italic"
                        style={{ color: m.pending ? "#FFB020" : m.coming_soon ? "#52525b" : "#71717A" }}
                      >
                        {m.status}
                      </div>
                    </div>
                    {(m.coming_soon || m.notify) && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setNotify({ open: true, make: m.name });
                        }}
                        className="shrink-0 inline-flex items-center gap-1 rounded-md border border-white/10 hover:border-white/30 hover:bg-white/[0.04] text-[9px] font-mono uppercase tracking-[0.14em] text-zinc-400 hover:text-white px-1.5 py-1 transition-colors"
                        aria-label={`Notify me when ${m.name} launches`}
                        data-testid={`notify-me-${m.name.toLowerCase().split(" ")[0]}`}
                        style={{ opacity: 1 }}
                      >
                        <Bell className="w-2.5 h-2.5" strokeWidth={2.4} />
                        Notify
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Small AI credits — legal / transparency */}
            <div className="mt-8 pt-4 border-t border-white/5" data-testid="about-credits">
              <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-zinc-600 mb-1.5">
                Technology credits
              </div>
              <p className="text-[11px] text-zinc-600 leading-relaxed">
                EVIQ AI Engine™ is built on proprietary analytical models
                combined with best-in-class large language model infrastructure.
                Battery physics models draw on published Tesla pack chemistry
                research and peer-reviewed Li-ion aging studies. All analytical
                scoring, anomaly detection, and forecasting logic is proprietary
                to EVIQ.
              </p>
            </div>
          </div>
        </div>
      </div>
      <NotifyMeModal
        open={notify.open}
        make={notify.make}
        onClose={() => setNotify({ open: false, make: "" })}
      />
    </section>
  );
};

const Pillar = ({ icon: Icon, label, desc }) => (
  <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5 hover:border-white/20 transition-colors">
    <Icon className="w-5 h-5 mb-3" strokeWidth={1.6} style={{ color: "#39FF14" }} />
    <div className="text-sm text-white font-medium">{label}</div>
    <div className="text-xs text-zinc-500 mt-1.5 leading-relaxed">{desc}</div>
  </div>
);
